import moment from 'moment';

export const SUSPICIOUS_FUTURE_DAYS = 7;
export const ALLOWED_PAST_DAYS = 14;

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const YMD_FORMAT = 'YYYY-MM-DD';

// Normalize any supported input (moment, Date, or 'YYYY-MM-DD' string) to
// { year, month, day } with month in 1-12, or null when unparseable.
function toYmdParts(value) {
  if (value == null || value === '') return null;

  if (moment.isMoment(value)) {
    if (!value.isValid()) return null;
    return { year: value.year(), month: value.month() + 1, day: value.date() };
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return {
      year: value.getFullYear(),
      month: value.getMonth() + 1,
      day: value.getDate(),
    };
  }

  if (typeof value === 'string') {
    const datePart = value.trim().split('T')[0];
    const parsed = moment(datePart, YMD_FORMAT, true);
    if (!parsed.isValid()) return null;
    return {
      year: parsed.year(),
      month: parsed.month() + 1,
      day: parsed.date(),
    };
  }

  return null;
}

function partsToDate({ year, month, day }) {
  return new Date(year, month - 1, day);
}

function formatYmd({ year, month, day }) {
  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

// Signed whole-day offset of `date` from `today`; future is positive.
function signedDaysFromToday(today, date) {
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const base = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((day.getTime() - base.getTime()) / MS_PER_DAY);
}

function isWithinWindow(offset) {
  return offset >= -ALLOWED_PAST_DAYS && offset <= SUSPICIOUS_FUTURE_DAYS;
}

/**
 * Returns a suggested 'YYYY-MM-DD' string if `value` looks like a month/day
 * transposition relative to `today`, otherwise null. `value` accepts a moment
 * object, a Date object, a 'YYYY-MM-DD' string, or a 'datetime-local'
 * ('YYYY-MM-DDTHH:mm') string; `today` is a Date.
 *
 * Warns when the entered date falls OUTSIDE the acceptable window
 * [today - ALLOWED_PAST_DAYS, today + SUSPICIOUS_FUTURE_DAYS] while its
 * month/day swap falls INSIDE it. An in-window entry is never second-guessed.
 */
export function getDateTranspositionSuggestion(value, today = new Date()) {
  const parts = toYmdParts(value);
  if (!parts) return null;

  const { year, month, day } = parts;

  // A month/day swap is only possible when both are <= 12 and they differ.
  if (month > 12 || day > 12 || month === day) return null;

  // Both values are <= 12, so the swap is always a valid calendar date.
  const swapped = { year, month: day, day: month };

  const enteredOffset = signedDaysFromToday(today, partsToDate(parts));
  const swappedOffset = signedDaysFromToday(today, partsToDate(swapped));

  if (!isWithinWindow(enteredOffset) && isWithinWindow(swappedOffset)) {
    return formatYmd(swapped);
  }
  return null;
}
